import Order, { AccessoryOrderStatus, OrderNotificationChannels, ShippingAddress } from './order';

/**
 * The course CRM's five shipping buckets — DERIVED server-side from the
 * fulfilment order, never stored. Mirrors the API's `CourseCrmShippingStatus`.
 */
export type CourseCrmShippingStatus = 'not_started' | 'awaiting_shipment' | 'shipped' | 'delivered' | 'needs_attention';

export const COURSE_CRM_SHIPPING_STATUSES: CourseCrmShippingStatus[] = [
    'not_started',
    'awaiting_shipment',
    'shipped',
    'delivered',
    'needs_attention',
];

export const COURSE_CRM_STATUS_LABELS: Record<CourseCrmShippingStatus, string> = {
    not_started: 'Not started',
    awaiting_shipment: 'Awaiting shipment',
    shipped: 'Shipped',
    delivered: 'Delivered',
    needs_attention: 'Needs attention',
};

/** One-line explanation shown under each bucket, so the words are unambiguous. */
export const COURSE_CRM_STATUS_HINTS: Record<CourseCrmShippingStatus, string> = {
    not_started: 'No address collected yet, or not handed to the shipper',
    awaiting_shipment: 'With the shipping partner, no tracking code yet',
    shipped: 'Tracking code on file, in transit',
    delivered: 'Confirmed delivered',
    needs_attention: 'Off the happy path — always paired with a note saying why',
};

export type CrmEnrollmentType = 'paid' | 'gift';

export interface CourseCrmChannelAvailability {
    enabled: boolean;
    available: boolean;
    reason?: string;
    target?: string;
}

export interface CourseCrmClient {
    userId: string;
    email: string;
    name: string;
    phone?: string;
    address?: ShippingAddress;
    country?: string;
    enrollmentType?: CrmEnrollmentType;

    cohortId?: string;
    cohortTitle?: string;
    /** Real enrollment title, else the manual `User.cohort` label. */
    cohortLabel?: string;

    referral?: string;
    linkedinUrl?: string;
    telegramHandle?: string;
    telegramUserId?: number;
    telegramJoined: boolean;
    telegramJoinedAt?: string;
    platformSignupAt: string;

    testimonialVideo: boolean;
    testimonialPost: boolean;
    /** Internal note about the PERSON (`User.adminNotes`). */
    notes?: string;
    /** Internal note about the SHIPMENT (`AccessoryOrder.adminNote`). Different owner. */
    shipmentNote?: string;

    shippingStatus: CourseCrmShippingStatus;
    orderStatus?: AccessoryOrderStatus;
    orderId?: string;
    orderNumber?: string;
    trackingCode?: string;
    carrier?: string;
    trackingUrl?: string;
    /**
     * Live carrier status, when auto-tracking is configured and has reported.
     * ABSENT is the normal state today — no provider has credentials yet — so
     * every column and panel must read as complete without it.
     */
    trackingStatusDetail?: string;
    trackingLastCheckedAt?: string;
    /** Why the last poll failed. Staff-only. */
    trackingLastError?: string;
    shippedAt?: string;
    deliveredAt?: string;
    needsAttention: boolean;
    notifications?: OrderNotificationChannels;
}

export interface CourseCrmSummary {
    total: number;
    paid: number;
    gift: number;
    notStarted: number;
    awaitingShipment: number;
    shipped: number;
    delivered: number;
    needsAttention: number;
    missingTrackingCode: number;
    unreachable: number;
}

export interface CourseCrmListResult {
    clients: CourseCrmClient[];
    summary: CourseCrmSummary;
    total: number;
    page: number;
    limit: number;
    cohorts: { id: string; title: string }[];
}

/** One entry of the client's lifecycle timeline (the API's `CrmActivity`). */
export interface CourseCrmActivity {
    id: string;
    type: string;
    summary: string;
    occurredAt: string;
    created_at: string;
    metadata?: Record<string, string | number | boolean>;
    actor?: { kind: string; adminEmail?: string };
}

export interface CourseCrmClientDetail {
    client: CourseCrmClient;
    /** The underlying fulfilment order — the same document the Orders screen edits. */
    order: Order | null;
    channels: Record<'email' | 'telegram' | 'inApp', CourseCrmChannelAvailability> | null;
    activity: CourseCrmActivity[];
    telegram: { chatId?: number; source?: 'bot_dm' | 'channel_invite'; handle?: string };
    /** Present only on the response to a shipment save. */
    notifications?: { channel: string; outcome: string; reason?: string }[];
}

export interface CourseCrmImportRowResult {
    rowNumber: number;
    name?: string;
    email?: string;
    action: 'created' | 'updated' | 'skipped' | 'error';
    changes: string[];
    warnings: string[];
    userId?: string;
}

export interface CourseCrmImportReport {
    dryRun: boolean;
    totalRows: number;
    created: number;
    updated: number;
    skipped: number;
    errors: number;
    unknownHeaders: string[];
    duplicateEmails: { email: string; rows: number[] }[];
    duplicatePhones: { phone: string; rows: number[] }[];
    notes: string[];
    rows: CourseCrmImportRowResult[];
}
